import {isDataClassificationError, isMissingDataGroupError} from 'common/report/queries/utils';
import {renamePropertyValue} from 'common/report/util/properties';
import {LoadingStatus} from 'common/report/util/retention/types';
import {RetentionQuery} from 'common/report/data/retention/retention-query';
import {
  ErrorResponse,
  isErrorResponse,
  isRetentionResponse,
  isSegmented,
  KeyedByDate,
  KeyedBySegment,
  RetentionData,
  RetentionQueryResponse,
  SuccessResponse,
} from 'common/report/data/retention/client';

/**
 * Transform the API response format with a multseg tree to a tree with eactly 3 levels
 * date, flattened segements, retention/addiction data.
 **/
function flattenSegments(response: KeyedByDate<any>, query: RetentionQuery): KeyedByDate<any> {
  function flatten(segments: KeyedBySegment<any>, depth: number, prefix = null): KeyedBySegment<any> {
    const flattened = {};
    for (const [segment, data] of Object.entries(segments)) {
      const renamedSegment = renamePropertyValue(
        segment,
        query.getGroupByName(query.groupBy[query.groupBy.length - depth]),
      );
      const key = prefix ? `${prefix} / ${renamedSegment}` : renamedSegment;
      if (depth === 1) {
        flattened[key] = data;
      } else {
        Object.assign(flattened, flatten(data, depth - 1, key));
      }
    }

    return flattened;
  }

  for (const [date, segments] of Object.entries(response)) {
    response[date] = flatten(segments, query.groupBy.length);
  }

  return response;
}

/** Parse a raw response into AppState, renaming segments as well. */
export function processQueryResponse(
  response: RetentionQueryResponse,
  query: RetentionQuery,
): {
  status: LoadingStatus;
  error: ErrorResponse;
  data: RetentionData;
  samplingFactor: number;
  computedAt: string;
  metadata?: SuccessResponse['metadata'];
} {
  if (isErrorResponse(response)) {
    let error: ErrorResponse = null;
    let status: LoadingStatus = `loading`;
    if (isDataClassificationError(response.error)) {
      status = `dataClassificationError`;
      error = {error: response.error};
    } else if (isMissingDataGroupError(response.error)) {
      status = `missingMappedDataGroup`;
      error = {error: response.error, data: response.data};
    } else {
      console.error(`Query returned an error: `, response.message);
      status = `error`;
      error = {error: response.message};
    }
    return {
      status,
      error,
      data: null,
      samplingFactor: null,
      computedAt: null,
    };
  } else {
    let responseData = response.results || response.data;
    if (isSegmented(responseData, query.isSegmented())) {
      // eslint-disable-next-line sonarjs/no-all-duplicated-branches
      if (isRetentionResponse(responseData, query.retentionType !== `addiction`)) {
        // Without this type narrowing if-statement, renameSegments causes a TypeScript error:
        // Argument of type 'KeyedByDate<KeyedBySegment<Addiction>> | KeyedByDate<KeyedBySegment<Retention>>'
        // is not assignable to parameter of type 'KeyedByDate<KeyedBySegment<Addiction>>'.
        responseData = flattenSegments(responseData, query);
      } else {
        responseData = flattenSegments(responseData, query);
      }
    }
    return {
      status: `complete`,
      samplingFactor: response.metadata?.min_sampling_factor || null,
      computedAt: response.computed_at,
      data: responseData,
      metadata: response.metadata,
      error: null,
    };
  }
}
